/**
 * Inventories (I1, I3, I4) and the reports built on the journal (R4, R7). Nothing here is stored
 * as a difference: every number is computed from the bookings each time, so late bookings from
 * an offline device change the result the next time it is computed (docs/04-domaenenmodell.md).
 *
 * An inventory is master data of its own (entity type "inventory"): name, storages, blind or not,
 * open or closed, and on closing the frozen report. Counts belong to it through `inventory`.
 * A known cause ("2 Fl. Bruch") is a real issue booked just before the first count of that wine
 * at that location, marked with the note `inventory:<id>`; what remains of the difference is
 * shrinkage.
 */
import { computeState } from "./ledger.ts";
import type { ComputeOptions, State, StockLine } from "./ledger.ts";
import { stockLines } from "./ledger.ts";
import type { Entity } from "./entities.ts";
import type { Booking, Id, Millis } from "./model.ts";

export interface Inventory {
  id: Id;
  name: string;
  /** Storages counted. */
  lagers: Id[];
  blind: boolean;
  startedAt: Millis;
  status: "open" | "closed";
  closedAt?: Millis;
  /** Frozen on closing. */
  report?: InventoryReport;
  /** Changes noticed after closing (late bookings), each with when it was noticed. */
  changes?: InventoryChange[];
}

export interface InventoryChange {
  article: Id;
  location: Id;
  before: number;
  after: number;
  noticedAt: Millis;
}

export function inventoryOf(e: Entity): Inventory | null {
  if (e.type !== "inventory") return null;
  const f = e.fields;
  return {
    id: e.id,
    name: typeof f.name === "string" ? f.name : "Inventur",
    lagers: Array.isArray(f.lagers) ? (f.lagers as Id[]) : [],
    blind: f.blind !== false,
    startedAt: typeof f.startedAt === "number" ? f.startedAt : 0,
    status: f.status === "closed" ? "closed" : "open",
    ...(typeof f.closedAt === "number" ? { closedAt: f.closedAt } : {}),
    ...(f.report && typeof f.report === "object" ? { report: f.report as InventoryReport } : {}),
    ...(Array.isArray(f.changes) ? { changes: f.changes as InventoryChange[] } : {}),
  };
}

/** The note on an issue that explains part of an inventory difference. */
export const causeNote = (inventory: Id) => `inventory:${inventory}`;

export interface Cause {
  booking: Id;
  reason: Id;
  qty: number;
  /** Value of the bottles (cents, positive). */
  value: number;
}

export interface InventoryRow {
  article: Id;
  lager: Id;
  location: Id;
  /** Latest count in this inventory. */
  counted: number;
  /** Stock by the journal before the counts, after the known causes. */
  expected: number;
  /** The whole difference, before the known causes are taken out (counted − stock without them). */
  diff: number;
  value: number;
  causes: Cause[];
  /** What is left: negative is shrinkage, positive more than booked. */
  rest: number;
  restValue: number;
  /** Time of the first count of this wine at this location in the inventory. */
  firstAt: Millis;
  lastAt: Millis;
}

export interface InventoryReport {
  rows: InventoryRow[];
  /** Wines with stock in the inventory's storages that nobody counted. */
  uncounted: { article: Id; location: Id; qty: number }[];
  totals: { diff: number; value: number; causes: number; causesValue: number; rest: number; restValue: number };
  /** When the report was computed. */
  at: Millis;
}

const pairKey = (article: Id, location: Id) => `${article}|${location}`;

/**
 * The report of an inventory from the computed state and the journal it was computed from.
 * Several counts of a wine at a location (a recount) add up: the differences of all of them are
 * the difference of the inventory.
 */
export function inventoryReport(state: State, bookings: Iterable<Booking>, inventory: Inventory, now: Millis): InventoryReport {
  const rows = new Map<string, InventoryRow>();
  for (const c of state.counts.values()) {
    if (c.inventory !== inventory.id) continue;
    const key = pairKey(c.article, c.location);
    const row = rows.get(key);
    if (!row) {
      rows.set(key, { article: c.article, lager: c.lager, location: c.location, counted: c.counted, expected: c.expected, diff: c.diff, value: c.value, causes: [], rest: c.diff, restValue: c.value, firstAt: c.at, lastAt: c.at });
      continue;
    }
    row.diff += c.diff;
    row.value += c.value;
    row.rest += c.diff;
    row.restValue += c.value;
    if (c.at >= row.lastAt) [row.counted, row.lastAt] = [c.counted, c.at];
    if (c.at < row.firstAt) [row.expected, row.firstAt] = [c.expected, c.at];
  }
  const note = causeNote(inventory.id);
  for (const b of bookings) {
    if (b.kind !== "out" || b.note !== note || state.cancelled.has(b.id)) continue;
    const row = rows.get(pairKey(b.article, b.location));
    if (!row) continue;
    const value = state.valuations.get(b.id)?.value ?? 0;
    row.causes.push({ booking: b.id, reason: b.reason, qty: b.qty, value });
    // The cause lowered the stock before the count: the whole difference includes it.
    row.diff -= b.qty;
    row.value -= value;
    row.expected += b.qty;
  }
  const scope = new Set(inventory.lagers);
  const stock = new Map<string, { article: Id; location: Id; qty: number }>();
  for (const line of stockLines(state)) {
    if (!scope.has(line.lager)) continue;
    const key = pairKey(line.article, line.location);
    const s = stock.get(key) ?? { article: line.article, location: line.location, qty: 0 };
    s.qty += line.qty;
    stock.set(key, s);
  }
  const uncounted = [...stock.entries()].filter(([key, s]) => s.qty !== 0 && !rows.has(key)).map(([, s]) => s);
  const list = [...rows.values()].sort((a, b) => a.restValue - b.restValue || a.article.localeCompare(b.article));
  const sum = (f: (r: InventoryRow) => number) => list.reduce((s, r) => s + f(r), 0);
  return {
    rows: list,
    uncounted,
    totals: {
      diff: sum((r) => r.diff),
      value: sum((r) => r.value),
      causes: sum((r) => r.causes.reduce((s, c) => s + c.qty, 0)),
      causesValue: sum((r) => r.causes.reduce((s, c) => s + c.value, 0)),
      rest: sum((r) => r.rest),
      restValue: sum((r) => r.restValue),
    },
    at: now,
  };
}

/** Rows whose rest changed since the report was frozen: "Schwund Merlot ändert sich von 7 auf 6". */
export function changedSinceClosing(frozen: InventoryReport, now: InventoryReport): { article: Id; location: Id; before: number; after: number }[] {
  const out: { article: Id; location: Id; before: number; after: number }[] = [];
  const current = new Map(now.rows.map((r) => [pairKey(r.article, r.location), r]));
  for (const r of frozen.rows) {
    const c = current.get(pairKey(r.article, r.location));
    const after = c?.rest ?? 0;
    if (after !== r.rest) out.push({ article: r.article, location: r.location, before: r.rest, after });
  }
  return out;
}

/**
 * The protocol of changes after closing: the noticed ones kept, new ones added with the time they
 * were noticed. Returns null when nothing is new (then nothing needs to be written).
 */
export function noteChanges(known: readonly InventoryChange[], now: { article: Id; location: Id; before: number; after: number }[], at: Millis): InventoryChange[] | null {
  const seen = new Set(known.map((c) => `${c.article}|${c.location}|${c.after}`));
  const fresh = now.filter((c) => !seen.has(`${c.article}|${c.location}|${c.after}`)).map((c) => ({ ...c, noticedAt: at }));
  return fresh.length ? [...known, ...fresh] : null;
}

export interface ShrinkageRow {
  article: Id;
  lager: Id;
  /** Sum of all count differences in the period: negative is shrinkage. */
  qty: number;
  value: number;
  /** The counts that found less than booked, summed apart (negative): never netted with `over`. */
  short: number;
  shortValue: number;
  /** The counts that found more than booked, summed apart (positive). */
  over: number;
  overValue: number;
  counts: number;
  /** Issues with a known loss as reason in the period (breakage, spoiled …), positive. */
  known: number;
  knownValue: number;
}

/**
 * Shrinkage per wine and storage in a period (R4): the differences of all counts, and next to it
 * the known losses booked. `knownLoss` tells which reasons count as known loss.
 */
export function shrinkageReport(state: State, bookings: Iterable<Booking>, from: Millis, to: Millis, lagers: ReadonlySet<Id> | null, knownLoss: (reason: Id) => boolean): ShrinkageRow[] {
  const rows = new Map<string, ShrinkageRow>();
  const row = (article: Id, lager: Id) => {
    const key = `${article}|${lager}`;
    let r = rows.get(key);
    if (!r) rows.set(key, (r = { article, lager, qty: 0, value: 0, short: 0, shortValue: 0, over: 0, overValue: 0, counts: 0, known: 0, knownValue: 0 }));
    return r;
  };
  for (const c of state.counts.values()) {
    if (c.at < from || c.at > to || (lagers && !lagers.has(c.lager))) continue;
    const r = row(c.article, c.lager);
    r.qty += c.diff;
    r.value += c.value;
    if (c.diff < 0) {
      r.short += c.diff;
      r.shortValue += c.value;
    } else if (c.diff > 0) {
      r.over += c.diff;
      r.overValue += c.value;
    }
    r.counts += 1;
  }
  for (const b of bookings) {
    if (b.kind !== "out" || b.at < from || b.at > to || state.cancelled.has(b.id) || (lagers && !lagers.has(b.lager)) || !knownLoss(b.reason)) continue;
    const r = row(b.article, b.lager);
    r.known += b.qty;
    r.knownValue += state.valuations.get(b.id)?.value ?? 0;
  }
  return [...rows.values()].filter((r) => r.short !== 0 || r.over !== 0 || r.known !== 0).sort((a, b) => a.value - b.value || b.knownValue - a.knownValue);
}

/**
 * Stock lines as they stood at a time (R7), from the whole journal. A cancel counts back to the
 * start (docs/04-domaenenmodell.md, Storno): a booking cancelled later is not in the stock at that
 * time either.
 */
export function stockAt(bookings: Booking[], at: Millis, opts: ComputeOptions = {}): StockLine[] {
  const until = bookings.filter((b) => b.kind === "cancel" || b.at <= at);
  return stockLines(computeState(until, opts));
}
